import { core, parseDay } from '@map-my-money/engine';
import type { AffordabilityResult, Comparison, WhatIfViewResult } from '@map-my-money/views';
import { useCallback, useMemo, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { CompareChart } from '../components/charts/CompareChart.tsx';
import { ChatBanner, ResultStamp } from '../components/ChatLink.tsx';
import { PageHeader } from '../components/Shell.tsx';
import { SectionHeader } from '../components/ui.tsx';
import { t, type MessageKey } from '../i18n/index.ts';
import { replayPurchase, replayWhatIf, type ReplayRequest } from '../lib/chatReplay.ts';
import { todayString } from '../lib/clock.ts';
import { compactMoney, currencySymbol, fullDate, money, monthYear, span } from '../lib/format.ts';
import { useIsDesktop } from '../lib/hooks.ts';
import { useStore } from '../state/store.tsx';

type Replay = Extract<ReplayRequest, { kind: 'changes' | 'buy' }>;

interface Tile {
  readonly label: string;
  readonly value: ReactNode;
  readonly tone?: string;
  readonly testId?: string;
}

/** Stat tiles on desktop; the narrow summary strip on a phone, where three tiles do not fit. */
function Tiles({ tiles, desktop }: { tiles: readonly Tile[]; desktop: boolean }) {
  if (!desktop) {
    return (
      <dl className="summary-strip">
        {tiles.map((tile) => (
          <div key={tile.label}>
            <dt>{tile.label}</dt>
            <dd className={tile.tone ?? ''} data-testid={tile.testId}>{tile.value}</dd>
          </div>
        ))}
      </dl>
    );
  }
  return (
    <div className="stat-tiles">
      {tiles.map((tile) => (
        <div key={tile.label} className="stat-tile">
          <span className="stat-tile__label">{tile.label}</span>
          <span className={`stat-tile__value ${tile.tone ?? ''}`} data-testid={tile.testId}>{tile.value}</span>
        </div>
      ))}
    </div>
  );
}

const sentence = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** A change set's line in the person's currency and dates: "… from 2029-12-30" reads "… from 30 Dec 2029". */
const readableLine = (line: string) =>
  sentence(line.replaceAll('£', currencySymbol()).replace(/\b(\d{4}-\d{2}-\d{2})\b/g, (day) => fullDate(parseDay(day))));

/** The What If screen replaying a chat's run_what_if or check_affordability: the same result, recomputed here with the same functions. */
export function WhatIfReplay({ request }: { request: Replay }) {
  const { plan, waypoints } = useStore();
  const desktop = useIsDesktop();
  const from = new URLSearchParams(useLocation().search).get('from');
  const today = todayString();
  const resultAt = useCallback(
    (asOf: string): WhatIfViewResult | AffordabilityResult | null => {
      if (!plan) return null;
      return request.kind === 'changes' ? replayWhatIf(plan, waypoints, asOf, request.changes) : replayPurchase(plan, waypoints, asOf, request);
    },
    [plan, waypoints, request],
  );
  const result = useMemo(() => resultAt(today), [resultAt, today]);
  if (!plan) return null;

  const title = request.kind === 'buy' ? t('buy.title', { item: request.item }) : t('whatIf.title');
  const startOver = <Link to="/what-if" className="button button--outline" data-testid="replay-start-over">{t('chat.replay.startOver')}</Link>;
  return (
    <div className={desktop ? 'page whatif-replay stack-lg' : 'page page--mobile whatif-replay stack-md'} data-testid="what-if-replay" data-kind={request.kind}>
      {desktop ? (
        <PageHeader eyebrow={request.kind === 'buy' ? t('buy.eyebrow') : from === 'try' ? t('whatIf.eyebrow') : from === 'move' ? t('whatIf.move.eyebrow') : t('chat.replay.eyebrow')} title={title} actions={startOver} />
      ) : (
        <header className="mobile-header"><h1 className="mobile-title">{title}</h1>{startOver}</header>
      )}
      <ChatBanner recompute={resultAt} note={result ? null : t('chat.replay.notApplicable')} />
      {result && 'comparison' in result && <ChangesReplay view={result} asOf={today} desktop={desktop} from={from} />}
      {result && 'if_bought' in result && request.kind === 'buy' && (
        <PurchaseReplay view={result} purchaseDate={request.purchaseDate ?? today} asOf={today} desktop={desktop} />
      )}
      <ResultStamp result={result} />
    </div>
  );
}

const CHANGES_TITLE: Readonly<Record<string, MessageKey>> = { try: 'whatIf.changes', move: 'whatIf.move.changes' };

function ChangesReplay({ view, asOf, desktop, from }: { view: WhatIfViewResult; asOf: string; desktop: boolean; from: string | null }) {
  return (
    <>
      <section className="card card--pad-lg" aria-labelledby="replay-changes">
        <SectionHeader title={t(CHANGES_TITLE[from ?? ''] ?? 'chat.replay.changes')} id="replay-changes" />
        <ul className="impact-list" data-testid="replay-changes">
          {view.changes.map((c) => (
            <li key={c} className="impact-list__item"><span className="impact-list__label">{readableLine(c)}</span></li>
          ))}
        </ul>
      </section>
      <ComparisonCard c={view.comparison} asOf={asOf} desktop={desktop} />
      <GoalsMoved c={view.comparison} />
    </>
  );
}

function ComparisonCard({ c, asOf, desktop }: { c: Comparison; asOf: string; desktop: boolean }) {
  const start = core.startOfMonth(parseDay(asOf));
  const line = (key: 'before' | 'after') => c.yearly.map((y) => ({ date: core.addMonths(start, y.year * 12), value: Number(y[key]) }));
  const change = Number(c.net_worth_change);
  return (
    <section className="card card--bento whatif-compare" aria-label={t('whatIf.withChanges')} data-testid="replay-comparison">
      <CompareChart
        baseline={line('before')}
        modified={line('after')}
        height={desktop ? 360 : 240}
        compact={!desktop}
        zoom
        label={t('chat.replay.chart', { years: c.years, baseline: money(Number(c.final_net_worth_before)), modified: money(Number(c.final_net_worth_after)) })}
      />
      <Tiles
        desktop={desktop}
        tiles={[
          { label: t('chat.replay.before', { years: c.years }), value: compactMoney(Number(c.final_net_worth_before)), testId: 'replay-before' },
          { label: t('chat.replay.after', { years: c.years }), value: compactMoney(Number(c.final_net_worth_after)), tone: 'text-cyber', testId: 'replay-after' },
          {
            label: t('chat.replay.difference'),
            value: <><span aria-hidden="true">{change >= 0 ? '↗ ' : '↘ '}</span>{compactMoney(change, { sign: true })}</>,
            tone: change >= 0 ? 'text-mint' : 'text-amber',
            testId: 'replay-difference',
          },
        ]}
      />
    </section>
  );
}

function goalShift(g: Comparison['goals'][number], years: number) {
  if (g.becomes_reachable) return <span className="impact impact--up">{t('whatIf.nowReachable')}</span>;
  if (g.becomes_unreachable) return <span className="impact impact--down">{t('chat.replay.goalUnreachable', { years })}</span>;
  if (!g.months_sooner) return <span className="impact impact--none">{t('common.noChange')}</span>;
  if (g.months_sooner > 0) return <span className="impact impact--up"><span aria-hidden="true">↗ </span>{t('whatIf.faster', { span: span(g.months_sooner) })}</span>;
  return <span className="impact impact--down"><span aria-hidden="true">↘ </span>{t('whatIf.slower', { span: span(-g.months_sooner) })}</span>;
}

function GoalsMoved({ c }: { c: Comparison }) {
  const moved = c.goals.filter((g) => c.goals_moved.some((m) => m.id === g.id));
  return (
    <section className="card card--pad-lg" aria-labelledby="replay-goals">
      <SectionHeader title={t('chat.replay.goals')} id="replay-goals" />
      {moved.length === 0 ? (
        <p className="muted">{t('chat.replay.noGoalsMove')}</p>
      ) : (
        <ul className="impact-list" data-testid="replay-goals">
          {moved.map((g) => (
            <li key={g.id} className="impact-list__item">
              <span className="impact-list__label">{g.label}{g.after ? ` · ${monthYear(parseDay(g.after))}` : ''}</span>
              {goalShift(g, c.years)}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function PurchaseReplay({ view, purchaseDate, asOf, desktop }: { view: AffordabilityResult; purchaseDate: string; asOf: string; desktop: boolean }) {
  return (
    <>
      <section className={desktop ? 'card card--pad-lg' : ''} aria-label={t('buy.eyebrow')} data-testid="replay-purchase">
        <Tiles
          desktop={desktop}
          tiles={[
            { label: t('buy.price'), value: money(Number(view.price)), testId: 'buy-price' },
            {
              label: t('buy.share'),
              value: view.percent_of_monthly_disposable === null ? t('buy.shareNone') : `${view.percent_of_monthly_disposable}%`,
              tone: 'text-amber',
              testId: 'buy-share',
            },
            { label: t('buy.disposable'), value: money(Number(view.monthly_disposable)), tone: 'text-mint' },
          ]}
        />
      </section>
      <section className="card card--pad-lg" aria-labelledby="buy-save">
        <SectionHeader title={t('buy.save')} id="buy-save" />
        <ul className="impact-list" data-testid="buy-save">
          {view.months_to_save.map((m) => (
            <li key={m.percent_of_disposable} className="impact-list__item">
              <span className="impact-list__label">{t('buy.saveRow', { percent: m.percent_of_disposable, amount: money(Number(m.monthly_amount)) })}</span>
              <span className="impact impact--none">{m.months === null ? t('buy.overTenYears') : span(m.months)}</span>
            </li>
          ))}
        </ul>
      </section>
      <section className="card card--pad-lg" aria-labelledby="buy-invested">
        <SectionHeader title={t('buy.invested', { rate: view.annual_return_percent })} id="buy-invested" />
        <ul className="impact-list" data-testid="buy-invested">
          {view.opportunity_cost.map((o) => (
            <li key={o.years} className="impact-list__item">
              <span className="impact-list__label">{o.years === 1 ? t('buy.investedRowOne') : t('buy.investedRow', { years: o.years })}</span>
              <span className="impact impact--up">{t('buy.investedValue', { value: compactMoney(Number(o.invested_value)), growth: compactMoney(Number(o.growth_forgone)) })}</span>
            </li>
          ))}
        </ul>
      </section>
      <h2 className="section-title">{t('buy.ifBought', { date: monthYear(parseDay(purchaseDate)) })}</h2>
      <ComparisonCard c={view.if_bought} asOf={asOf} desktop={desktop} />
      <GoalsMoved c={view.if_bought} />
    </>
  );
}
